import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Loader2, OctagonX, Play, RotateCcw, Search, ShieldCheck } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { staticBreadcrumbLabelForPath } from "@/lib/breadcrumbRouteLabels";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { IngestRunActivityCell } from "@/components/IngestRunActivityCell";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintContent, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import {
  INGEST_CONTROL_QUERY_LIVE,
  megaventoryReceiverSubscriptionApiPath,
  type IngestControlEntity,
  type IngestControlListResponse,
} from "@/lib/ingest-control-api";
import { ingestOwnerBadge } from "@/lib/ingest-control-display";
import { useNowTick } from "@/lib/useNowTick";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { useErpIngestLiveQueries } from "@/lib/useErpIngestLiveQueries";
import { formatTimestamp, ingestRunDurationLabel, relativeTime, cn } from "@/lib/utils";

interface IngestRun {
  id: number;
  entity: string;
  mode: string;
  started_at: string;
  ended_at: string | null;
  status: "running" | "success" | "partial" | "failed";
  records_seen: number;
  records_upserted: number;
  records_unchanged: number;
  records_skipped: number;
  records_failed: number;
  records_drift: number;
  records_missing: number;
  records_orphan: number;
  triggered_by: string | null;
  progress_phase?: string | null;
  progress_label?: string | null;
  progress_current?: number | null;
  progress_total?: number | null;
}

const MODES = [
  { value: "dry_run", label: "Dry run", icon: Play, description: "Fetch + map, write nothing." },
  { value: "diff", label: "Diff vs prod", icon: Search, description: "Compare app's would-be writes to current Supabase rows." },
  { value: "live", label: "Live (cutover)", icon: ShieldCheck, description: "Only allowed if owner = app_only." },
  {
    value: "sweep_report",
    label: "Sweep report",
    icon: Search,
    description:
      "Full pull from Megaventory; classify drift, missing, and entity-specific orphans. For GIPI/TSH, invoice drift may include unlinked purchase order line items (null purchase_order_item_id while a parent purchase order exists) — not the same as a Megaventory document that disappeared.",
  },
  {
    value: "sweep_apply",
    label: "Sweep apply",
    icon: ShieldCheck,
    description:
      "Same classification as sweep report, then write fixes for drift. Purchase invoice line links are resolved by ingest rules, not arbitrary SQL.",
  },
  { value: "replay", label: "Replay failed", icon: RotateCcw, description: "Re-run unresolved failed events." },
] as const;

const DATA_CONNECTORS_READ_ONLY_TITLE =
  "Your role has read-only access to the Data connectors hub module.";
const SWEEP_NOT_APPLICABLE_TITLE =
  "Sweep does not apply to this entity: each live ingest already replaces the full snapshot in the database, so row-by-row drift classification would add no value.";
const CLEAR_STUCK_RUN_HELP =
  "Marks this run as failed if it is still running in the database. Use when a process exited without finishing. This does not stop a worker process; if a job is still active, it may still write a final status when it completes.";

function runStatusTone(status: IngestRun["status"]): BadgeTone {
  switch (status) {
    case "success":
      return "success";
    case "partial":
      return "warning";
    case "failed":
      return "danger";
    case "running":
      return "info";
  }
}

export function EntityDetail() {
  const { entity } = useParams<{ entity: string }>();
  const qc = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("erp");
  const [busyMode, setBusyMode] = useState<string | null>(null);
  const [abortRunError, setAbortRunError] = useState<string | null>(null);

  const controlQuery = useQuery({
    queryKey: ["ingest-control", entity],
    queryFn: () => apiFetch<IngestControlListResponse>("/api/ingest-control"),
    ...INGEST_CONTROL_QUERY_LIVE,
  });
  useErpIngestLiveQueries(Boolean(entity));
  const row = controlQuery.data?.entities.find((entry) => entry.entity === entity);
  const entityPageLabel =
    entity != null && entity.length > 0
      ? staticBreadcrumbLabelForPath(`/erp/entity/${entity}`)
      : "Entity ingest";
  usePageBreadcrumb(entityPageLabel);
  /** If the API omits `registered_entities`, allow runs (older response shape). */
  const registeredInApp =
    controlQuery.data?.registered_entities == null ||
    (entity != null && controlQuery.data.registered_entities.includes(entity));
  const sweepReportSupported = row != null ? row.sweep_report_supported !== false : true;

  const runsQuery = useQuery({
    queryKey: ["ingest-runs", entity],
    queryFn: () =>
      apiFetch<{ runs: IngestRun[] }>("/api/ingest-runs", {
        query: { entity, limit: 50, skip_total: true },
      }),
    enabled: Boolean(entity),
  });

  const entityRuns = runsQuery.data?.runs ?? [];
  const hasRunningRunRow = useMemo(
    () => entityRuns.some((run) => run.status === "running"),
    [entityRuns],
  );
  const durationClockMs = useNowTick(hasRunningRunRow || busyMode != null);

  const runMutation = useMutation({
    mutationFn: (mode: string) =>
      apiFetch("/api/run-now", { method: "POST", body: { entity, mode } }),
    onMutate: (mode: string) => {
      setBusyMode(mode);
      void qc.refetchQueries({ queryKey: ["ingest-runs", entity] });
    },
    onSettled: () => {
      setBusyMode(null);
      void qc.invalidateQueries({ queryKey: ["ingest-runs", entity] });
      void qc.invalidateQueries({ queryKey: ["ingest-control", entity] });
    },
  });

  const patchMutation = useMutation({
    mutationFn: (patch: Partial<IngestControlEntity>) =>
      apiFetch(`/api/ingest-control/${entity}`, { method: "PATCH", body: patch }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["ingest-control"] });
    },
  });

  const abortRunMutation = useMutation({
    mutationFn: (runId: number) =>
      apiFetch<{ run: { id: number } }>(`/api/ingest-runs/${runId}/abort`, { method: "POST" }),
    onMutate: () => {
      setAbortRunError(null);
    },
    onError: (err) => {
      setAbortRunError(err instanceof Error ? err.message : String(err));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["ingest-runs"] });
      void qc.invalidateQueries({ queryKey: ["ingest-control"] });
    },
  });

  const [megaventorySubscriptionFeedback, setMegaventorySubscriptionFeedback] = useState<
    { tone: "success"; message: string } | { tone: "error"; message: string } | null
  >(null);

  const ensureMegaventorySubscriptionMutation = useMutation({
    mutationFn: async () => {
      const found = controlQuery.data?.entities.find((entry) => entry.entity === entity);
      const slice = found?.megaventory_webhook;
      if (slice?.applicable !== true || slice.receiver == null) {
        throw new Error("Megaventory webhook registration is not applicable for this entity.");
      }
      const segment = megaventoryReceiverSubscriptionApiPath(slice.receiver);
      return apiFetch<{ megaventory_subscriptions: unknown }>(
        `/api/megaventory/webhook-subscriptions/${segment}`,
        { method: "POST", body: {} },
      );
    },
    onMutate: () => {
      setMegaventorySubscriptionFeedback(null);
    },
    onSuccess: () => {
      setMegaventorySubscriptionFeedback({
        tone: "success",
        message:
          "Megaventory EventSubscription rows were created or replaced for this integrator listener URL.",
      });
      void qc.invalidateQueries({ queryKey: ["ingest-control"] });
      if (entity) void qc.invalidateQueries({ queryKey: ["ingest-control", entity] });
    },
    onError: (err) => {
      setMegaventorySubscriptionFeedback({
        tone: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    },
  });

  const revokeMegaventorySubscriptionMutation = useMutation({
    mutationFn: async () => {
      const found = controlQuery.data?.entities.find((entry) => entry.entity === entity);
      const slice = found?.megaventory_webhook;
      if (slice?.applicable !== true || slice.receiver == null) {
        throw new Error("Megaventory webhook registration is not applicable for this entity.");
      }
      const segment = megaventoryReceiverSubscriptionApiPath(slice.receiver);
      return apiFetch<{ revoked: boolean }>(`/api/megaventory/webhook-subscriptions/${segment}`, {
        method: "DELETE",
      });
    },
    onMutate: () => {
      setMegaventorySubscriptionFeedback(null);
    },
    onSuccess: () => {
      setMegaventorySubscriptionFeedback({
        tone: "success",
        message:
          "Megaventory subscriptions for this listener URL were revoked and stored registration ids were cleared on the hub.",
      });
      void qc.invalidateQueries({ queryKey: ["ingest-control"] });
      if (entity) void qc.invalidateQueries({ queryKey: ["ingest-control", entity] });
    },
    onError: (err) => {
      setMegaventorySubscriptionFeedback({
        tone: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    },
  });

  if (!row) {
    return (
      <div className="space-y-4">
        <Link to="/erp/mv-ingest" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" />
          Ingest
        </Link>
        <p className="text-sm text-slate-600">{controlQuery.isLoading ? "Loading…" : "Entity not found."}</p>
      </div>
    );
  }

  const owner = ingestOwnerBadge(row.source, row.enabled);
  const megaventoryWebhook = row.megaventory_webhook;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/erp/mv-ingest" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
          <ChevronLeft className="h-4 w-4" />
          Ingest
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900">{row.entity}</h1>
          <HoverHintContent hoverHint={owner.title} className="inline-flex">
            <Badge tone={owner.tone}>{owner.label}</Badge>
          </HoverHintContent>
        </div>
        <p className="mt-1 text-sm text-slate-600">
          <Link
            to={`/erp/findings?entity=${encodeURIComponent(row.entity)}&outcome=problems`}
            className="font-medium text-brand-700 underline hover:text-brand-800"
          >
            Findings
          </Link>
          <span className="text-slate-500">
            {" "}
            ({row.open_problems_count ?? 0} unresolved for this entity — resolve or replay there)
          </span>
        </p>
        {row.notes ? <p className="mt-1 text-sm text-slate-500">{row.notes}</p> : null}
        {abortRunError ? (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {abortRunError}
          </p>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Cutover</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-slate-600">
              Owner:{" "}
              <HoverHintContent hoverHint={owner.title} className="inline-flex">
                <Badge tone={owner.tone}>{owner.label}</Badge>
              </HoverHintContent>
            </p>
            <p className="text-xs text-slate-500">
              Database: <code className="rounded bg-slate-100 px-1">source = {row.source}</code>
              {", "}
              <code className="rounded bg-slate-100 px-1">enabled = {row.enabled ? "true" : "false"}</code>
            </p>
            <div className="flex gap-2">
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                <Button
                  variant={row.source === "app_only" ? "secondary" : "primary"}
                  onClick={() => patchMutation.mutate({ source: row.source === "app_only" ? "n8n_only" : "app_only" })}
                  isLoading={patchMutation.isPending}
                  disabled={!hubWritesAllowed}
                >
                  {row.source === "app_only" ? "Roll back to n8n" : "Cut over to app"}
                </Button>
              </ReadOnlyHoverLayer>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                <Button
                  variant="secondary"
                  onClick={() => patchMutation.mutate({ enabled: !row.enabled })}
                  disabled={!hubWritesAllowed}
                >
                  {row.enabled ? "Disable" : "Enable"}
                </Button>
              </ReadOnlyHoverLayer>
            </div>
            <p className="text-xs text-slate-500">
              The scheduler hot-reloads this on its next tick; live runs refuse to start unless owner = app_only.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Run on demand</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2">
            {!registeredInApp ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                The running integrator API does not register this entity yet:{" "}
                <code className="rounded bg-amber-100 px-1">GET /api/ingest-control</code> →{" "}
                <code className="rounded bg-amber-100 px-1">registered_entities</code> does not include{" "}
                <code className="rounded bg-amber-100 px-1">{entity ?? "this key"}</code>. Deploy a build that imports
                the ingest in <code className="rounded bg-amber-100 px-1">src/ingest/register-all.ts</code>, restart the
                API and scheduler processes, then hard-refresh this page. (Your database row can be{" "}
                <code className="rounded bg-amber-100 px-1">app_only</code> before the server code catches up.)
              </p>
            ) : null}
            {MODES.map(({ value, label, icon: Icon, description }) => {
              const sweepModeBlocked =
                registeredInApp &&
                !sweepReportSupported &&
                (value === "sweep_report" || value === "sweep_apply");
              const runTip = !hubWritesAllowed
                ? DATA_CONNECTORS_READ_ONLY_TITLE
                : !registeredInApp
                  ? "Server build does not register this entity yet (see amber notice); deploy API + restart."
                  : sweepModeBlocked
                    ? SWEEP_NOT_APPLICABLE_TITLE
                    : null;
              const runButton = (
                <Button
                  size="sm"
                  variant={value === "live" ? "primary" : "secondary"}
                  isLoading={busyMode === value && runMutation.isPending}
                  disabled={!registeredInApp || !hubWritesAllowed || sweepModeBlocked}
                  onClick={() => runMutation.mutate(value)}
                >
                  Run
                </Button>
              );
              return (
              <div key={value} className="flex items-start justify-between gap-3 rounded-md border border-slate-100 p-2">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{description}</p>
                </div>
                {runTip ? (
                  <HoverHintTrigger align="center" trigger={runButton}>
                    <span>{runTip}</span>
                  </HoverHintTrigger>
                ) : (
                  runButton
                )}
              </div>
              );
            })}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tunables</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <TunableField
              label="Schedule cron"
              value={row.schedule_cron ?? ""}
              onSave={(value) => patchMutation.mutate({ schedule_cron: value || null })}
              hubWritesAllowed={hubWritesAllowed}
            />
            <div>
              <TunableField
                label="Sweep cron"
                value={row.sweep_schedule_cron ?? ""}
                onSave={(value) => patchMutation.mutate({ sweep_schedule_cron: value || null })}
                hubWritesAllowed={hubWritesAllowed}
              />
              {registeredInApp && !sweepReportSupported ? (
                <p className="mt-1 text-xs text-slate-500">
                  The scheduler does not run <code className="rounded bg-slate-100 px-1">sweep_report</code> for this
                  entity; clear this cron if you do not need a no-op tick.
                </p>
              ) : null}
            </div>
            <TunableField
              label="Watermark safety overlap (min)"
              value={String(row.watermark_safety_overlap_minutes)}
              onSave={(value) =>
                patchMutation.mutate({ watermark_safety_overlap_minutes: Number.parseInt(value, 10) })
              }
              hubWritesAllowed={hubWritesAllowed}
            />
            <TunableField
              label="Watermark advance lag (min)"
              value={String(row.watermark_advance_lag_minutes)}
              onSave={(value) =>
                patchMutation.mutate({ watermark_advance_lag_minutes: Number.parseInt(value, 10) })
              }
              hubWritesAllowed={hubWritesAllowed}
            />
            <TunableField
              label="Retry delay (sec)"
              value={String(row.retry_delay_seconds)}
              onSave={(value) => patchMutation.mutate({ retry_delay_seconds: Number.parseInt(value, 10) })}
              hubWritesAllowed={hubWritesAllowed}
            />
          </CardBody>
        </Card>
      </div>

      {megaventoryWebhook != null && megaventoryWebhook.applicable ? (
        <Card>
          <CardHeader>
            <CardTitle>Megaventory webhooks</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-slate-600">
              This entity uses Megaventory receiver{" "}
              <code className="rounded bg-slate-100 px-1 text-xs">{megaventoryWebhook.receiver}</code>
              {megaventoryWebhook.receiver === "document" ? (
                <>
                  {" "}
                  (<code className="rounded bg-slate-100 px-1 text-xs">/api/webhooks/megaventory/document</code>
                  {", shared with other document-backed ingests). "}
                </>
              ) : (
                ". "
              )}
              {megaventoryWebhook.subscriptions_registered ? (
                <>
                  {megaventoryWebhook.receiver_subscription_count != null &&
                  megaventoryWebhook.receiver_subscription_count !== megaventoryWebhook.subscription_count
                    ? `${megaventoryWebhook.subscription_count} of ${megaventoryWebhook.receiver_subscription_count} EventSubscription row${
                        megaventoryWebhook.receiver_subscription_count === 1 ? "" : "s"
                      } on the hub apply to this ingest entity (shared listener URL; the table lists only applicable event types).`
                    : `${megaventoryWebhook.subscription_count} EventSubscription row${
                        megaventoryWebhook.subscription_count === 1 ? "" : "s"
                      } stored on the hub for this entity’s event types on that listener URL.`}
                </>
              ) : megaventoryWebhook.receiver_subscription_count != null &&
                megaventoryWebhook.receiver_subscription_count > 0 ? (
                <>
                  No EventSubscription rows stored for this ingest entity’s event types. The hub has{" "}
                  {megaventoryWebhook.receiver_subscription_count} row
                  {megaventoryWebhook.receiver_subscription_count === 1 ? "" : "s"} for other event types on the same
                  listener URL.
                </>
              ) : (
                "No EventSubscription ids stored on the hub yet for this entity’s event types (run API startup ensure or the subscription ensure API)."
              )}
            </p>
            {megaventoryWebhook.subscriptions_updated_at ? (
              <p className="text-xs text-slate-500">
                Registration state last updated{" "}
                <span className="font-medium">{formatTimestamp(megaventoryWebhook.subscriptions_updated_at)}</span>
              </p>
            ) : null}
            {megaventoryWebhook.listener_url_masked ? (
              <p className="break-all text-xs text-slate-600">
                <span className="font-medium">Listener URL: </span>
                {megaventoryWebhook.listener_url_masked}
              </p>
            ) : null}
            {megaventoryWebhook.receiver === "document" ||
            (megaventoryWebhook.subscriptions?.length ?? 0) > 0 ? (
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Event type</th>
                      <th className="px-3 py-2 font-medium">Megaventory Event id</th>
                      {megaventoryWebhook.receiver === "document" ? (
                        <th className="px-3 py-2 font-medium">Document type id</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {(megaventoryWebhook.subscriptions?.length ?? 0) > 0 ? (
                      (megaventoryWebhook.subscriptions ?? []).map((sub) => (
                        <tr key={`${sub.event_type}-${sub.mv_event_id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-slate-800">{sub.event_type}</td>
                          <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-700">{sub.mv_event_id}</td>
                          {megaventoryWebhook.receiver === "document" ? (
                            <td className="px-3 py-2 font-mono text-xs text-slate-700">
                              {megaventoryWebhook.document_type_ids != null &&
                              megaventoryWebhook.document_type_ids.length > 0 ? (
                                <code className="rounded bg-slate-100 px-1">
                                  {megaventoryWebhook.document_type_ids.join(", ")}
                                </code>
                              ) : (
                                "—"
                              )}
                            </td>
                          ) : null}
                        </tr>
                      ))
                    ) : megaventoryWebhook.receiver === "document" ? (
                      <tr>
                        <td className="px-3 py-2 text-xs text-slate-500" colSpan={2}>
                          No EventSubscription rows stored for this entity yet on this listener URL.
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700">
                          {megaventoryWebhook.document_type_ids != null &&
                          megaventoryWebhook.document_type_ids.length > 0 ? (
                            <code className="rounded bg-slate-100 px-1">
                              {megaventoryWebhook.document_type_ids.join(", ")}
                            </code>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : null}
            {controlQuery.data?.megaventory_webhook_secret_configured === false ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This API host does not have{" "}
                <code className="rounded bg-amber-100 px-1">INTEGRATOR_MEGAVENTORY_WEBHOOK_SECRET</code> set; inbound
                Megaventory webhook routes are disabled here.
              </p>
            ) : null}
            {megaventoryWebhook.receiver != null ? (
              <div className="space-y-2 border-t border-slate-100 pt-3">
                {megaventorySubscriptionFeedback ? (
                  <p
                    className={
                      megaventorySubscriptionFeedback.tone === "success"
                        ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
                        : "rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
                    }
                    role={megaventorySubscriptionFeedback.tone === "error" ? "alert" : undefined}
                  >
                    {megaventorySubscriptionFeedback.message}
                  </p>
                ) : null}
                <p className="text-xs text-slate-500">
                  Register or revoke Megaventory EventSubscription rows for{" "}
                  <code className="rounded bg-slate-100 px-1">
                    /api/megaventory/webhook-subscriptions/{megaventoryReceiverSubscriptionApiPath(megaventoryWebhook.receiver)}
                  </code>
                  . The integrator also attempts this on API boot when{" "}
                  <code className="rounded bg-slate-100 px-1">INTEGRATOR_MEGAVENTORY_WEBHOOK_AUTO_ENSURE</code> is not
                  disabled.
                </p>
                <div className="flex flex-wrap gap-2">
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={
                        !hubWritesAllowed ||
                        controlQuery.data?.megaventory_webhook_secret_configured === false ||
                        ensureMegaventorySubscriptionMutation.isPending ||
                        revokeMegaventorySubscriptionMutation.isPending
                      }
                      isLoading={ensureMegaventorySubscriptionMutation.isPending}
                      onClick={() => ensureMegaventorySubscriptionMutation.mutate()}
                    >
                      Register with Megaventory
                    </Button>
                  </ReadOnlyHoverLayer>
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={
                        !hubWritesAllowed ||
                        controlQuery.data?.megaventory_webhook_secret_configured === false ||
                        ensureMegaventorySubscriptionMutation.isPending ||
                        revokeMegaventorySubscriptionMutation.isPending
                      }
                      isLoading={revokeMegaventorySubscriptionMutation.isPending}
                      onClick={() => {
                        if (
                          !window.confirm(
                            "Revoke Megaventory EventSubscription rows for this listener URL and clear stored subscription ids on the hub?",
                          )
                        ) {
                          return;
                        }
                        revokeMegaventorySubscriptionMutation.mutate();
                      }}
                    >
                      Revoke hub registration
                    </Button>
                  </ReadOnlyHoverLayer>
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">Mode</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Activity</th>
                <th className="px-4 py-2 font-medium">Triggered by</th>
                <th className="px-4 py-2 font-medium">Seen</th>
                <th className="px-4 py-2 font-medium">Upserted</th>
                <th className="px-4 py-2 font-medium">Unchanged</th>
                <th className="px-4 py-2 font-medium">Failed</th>
                <th className="px-4 py-2 font-medium">Drift / Missing / Orphan</th>
                <th className="px-4 py-2 font-medium">
                  <span className="sr-only">Clear stuck run</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {runsQuery.data?.runs.map((run) => (
                <tr key={run.id}>
                  <td className="px-4 py-2 text-slate-600">
                    <div>{formatTimestamp(run.started_at)}</div>
                    <div className="text-xs text-slate-400">{relativeTime(run.started_at)}</div>
                  </td>
                  <td className="px-4 py-2">
                    {(() => {
                      const { text, isLive } = ingestRunDurationLabel(run, durationClockMs);
                      return (
                        <HoverHintContent
                          hoverHint={
                            isLive
                              ? "Elapsed time since this run started (updates every second)."
                              : "Time from start to end."
                          }
                          className={cn(
                            "inline-block font-mono text-xs tabular-nums text-slate-700",
                            isLive && "font-medium text-brand-700",
                          )}
                        >
                          {text}
                          {isLive ? (
                            <Loader2 className="ml-1 inline h-3 w-3 animate-spin text-brand-600" aria-hidden />
                          ) : null}
                        </HoverHintContent>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{run.mode}</td>
                  <td className="px-4 py-2">
                    <Badge tone={runStatusTone(run.status)}>{run.status}</Badge>
                  </td>
                  <td className="px-4 py-2 align-top">
                    <IngestRunActivityCell run={run} />
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">{run.triggered_by ?? "—"}</td>
                  <td className="px-4 py-2">{run.records_seen}</td>
                  <td className="px-4 py-2">{run.records_upserted}</td>
                  <td className="px-4 py-2">{run.records_unchanged}</td>
                  <td className="px-4 py-2">
                    {run.records_failed > 0 ? (
                      <span className="font-medium text-red-700">{run.records_failed}</span>
                    ) : (
                      <span className="text-slate-500">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {run.records_drift} / {run.records_missing} / {run.records_orphan}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {run.status === "running" ? (
                      !hubWritesAllowed ? (
                        <ReadOnlyHoverLayer active hoverHint={DATA_CONNECTORS_READ_ONLY_TITLE}>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="whitespace-nowrap border-red-200 text-red-800 hover:bg-red-50"
                            disabled
                            isLoading={abortRunMutation.isPending && abortRunMutation.variables === run.id}
                            onClick={() => {
                              abortRunMutation.mutate(run.id);
                            }}
                          >
                            <OctagonX className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                            Clear stuck status
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : (
                        <HoverHintTrigger
                          align="center"
                          trigger={
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="whitespace-nowrap border-red-200 text-red-800 hover:bg-red-50"
                              aria-label={CLEAR_STUCK_RUN_HELP}
                              isLoading={abortRunMutation.isPending && abortRunMutation.variables === run.id}
                              onClick={() => {
                                abortRunMutation.mutate(run.id);
                              }}
                            >
                              <OctagonX className="mr-1 inline h-3.5 w-3.5" aria-hidden />
                              Clear stuck status
                            </Button>
                          }
                        >
                          <span>{CLEAR_STUCK_RUN_HELP}</span>
                        </HoverHintTrigger>
                      )
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

function TunableField({
  label,
  value,
  onSave,
  hubWritesAllowed,
}: {
  label: string;
  value: string;
  onSave: (value: string) => void;
  hubWritesAllowed: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const dirty = draft !== value;
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex gap-2">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={!hubWritesAllowed}
        />
        <HubSaveButton
          variant={dirty ? "primary" : "secondary"}
          size="sm"
          dirty={dirty}
          disabled={!hubWritesAllowed}
          blockedHint={DATA_CONNECTORS_READ_ONLY_TITLE}
          onClick={() => onSave(draft)}
        >
          Save
        </HubSaveButton>
      </div>
    </div>
  );
}
